import * as THREE from "three";
import { CHUNK_SIZE, UNLOAD_PADDING, VIEW_DISTANCE } from "../config";
import { meshChunk } from "../engine/mesher";
import type { Atlas } from "../engine/textures";
import { worldToChunk, type World } from "../engine/world";
import { chunkKey } from "../engine/chunk";
import { createWaterMaterial, patchWind } from "./shaders";

export class WorldView {
  readonly group = new THREE.Group();
  private meshes = new Map<string, THREE.Group>();
  private pending = new Set<string>();
  private worker: Worker;
  private solidMat: THREE.MeshLambertMaterial;
  private cutoutMat: THREE.MeshLambertMaterial;
  private waterMat: THREE.ShaderMaterial;
  private time = { value: 0 };
  private queue: { cx: number; cz: number }[] = [];
  loaded = 0;
  wanted = 0;

  constructor(
    private world: World,
    atlas: Atlas,
    worker: Worker,
  ) {
    this.worker = worker;
    this.solidMat = new THREE.MeshLambertMaterial({
      map: atlas.texture,
      vertexColors: true,
      fog: true,
    });
    this.cutoutMat = new THREE.MeshLambertMaterial({
      map: atlas.texture,
      vertexColors: true,
      fog: true,
      alphaTest: 0.35,
      side: THREE.DoubleSide,
      transparent: false,
    });
    patchWind(this.cutoutMat, this.time);
    this.waterMat = createWaterMaterial(this.time);

    this.worker.onmessage = (e: MessageEvent) => {
      const { cx, cz, blocks, biomes, heights } = e.data as {
        cx: number;
        cz: number;
        blocks: Uint16Array;
        biomes: Uint8Array;
        heights: Uint8Array;
      };
      const key = chunkKey(cx, cz);
      this.pending.delete(key);
      this.world.applyGenerated(cx, cz, blocks, biomes, heights);
      this.remesh(cx, cz);
      this.remesh(cx - 1, cz);
      this.remesh(cx + 1, cz);
      this.remesh(cx, cz - 1);
      this.remesh(cx, cz + 1);
    };
  }

  requestAround(x: number, z: number): void {
    const pcx = worldToChunk(x);
    const pcz = worldToChunk(z);
    this.queue.length = 0;
    const coords: { cx: number; cz: number; d: number }[] = [];
    for (let dz = -VIEW_DISTANCE; dz <= VIEW_DISTANCE; dz++) {
      for (let dx = -VIEW_DISTANCE; dx <= VIEW_DISTANCE; dx++) {
        if (Math.hypot(dx, dz) > VIEW_DISTANCE + 0.25) continue;
        coords.push({ cx: pcx + dx, cz: pcz + dz, d: dx * dx + dz * dz });
      }
    }
    coords.sort((a, b) => a.d - b.d);
    this.wanted = coords.length;
    for (const c of coords) {
      const key = chunkKey(c.cx, c.cz);
      const chunk = this.world.getChunk(c.cx, c.cz);
      if (chunk?.generated) continue;
      if (this.pending.has(key)) continue;
      this.queue.push({ cx: c.cx, cz: c.cz });
    }

    const removed = this.world.unloadFar(x, z, VIEW_DISTANCE + UNLOAD_PADDING);
    for (const key of removed) this.disposeKey(key);

    this.loaded = 0;
    for (const ch of this.world.chunks.values()) if (ch.generated) this.loaded++;
  }

  pump(max = 2): void {
    for (let i = 0; i < max && this.queue.length; i++) {
      const job = this.queue.shift()!;
      const key = chunkKey(job.cx, job.cz);
      if (this.pending.has(key)) continue;
      this.pending.add(key);
      this.world.ensureChunk(job.cx, job.cz);
      this.worker.postMessage({ cx: job.cx, cz: job.cz, seed: this.world.seed });
    }
  }

  remeshDirty(limit = 3): void {
    let n = 0;
    for (const chunk of this.world.chunks.values()) {
      if (!chunk.generated || !chunk.dirty) continue;
      this.remesh(chunk.cx, chunk.cz);
      n++;
      if (n >= limit) break;
    }
  }

  remesh(cx: number, cz: number): void {
    const chunk = this.world.getChunk(cx, cz);
    if (!chunk?.generated) return;
    const key = chunkKey(cx, cz);
    this.disposeKey(key);
    const built = meshChunk(this.world, chunk);
    const g = new THREE.Group();
    g.position.set(cx * CHUNK_SIZE, 0, cz * CHUNK_SIZE);
    if (built.solid) {
      const m = new THREE.Mesh(built.solid, this.solidMat);
      m.matrixAutoUpdate = false;
      m.updateMatrix();
      g.add(m);
    }
    if (built.cutout) {
      const m = new THREE.Mesh(built.cutout, this.cutoutMat);
      m.matrixAutoUpdate = false;
      m.updateMatrix();
      g.add(m);
    }
    if (built.water) {
      const m = new THREE.Mesh(built.water, this.waterMat);
      m.renderOrder = 2;
      m.matrixAutoUpdate = false;
      m.updateMatrix();
      g.add(m);
    }
    this.group.add(g);
    this.meshes.set(key, g);
    chunk.dirty = false;
    chunk.meshId++;
  }

  tick(t: number): void {
    this.time.value = t;
  }

  readyNear(x: number, z: number, radius = 2): boolean {
    const pcx = worldToChunk(x);
    const pcz = worldToChunk(z);
    for (let dz = -radius; dz <= radius; dz++) {
      for (let dx = -radius; dx <= radius; dx++) {
        const c = this.world.getChunk(pcx + dx, pcz + dz);
        if (!c?.generated) return false;
      }
    }
    return true;
  }

  dispose(): void {
    for (const key of [...this.meshes.keys()]) this.disposeKey(key);
    this.worker.terminate();
  }

  private disposeKey(key: string): void {
    const g = this.meshes.get(key);
    if (!g) return;
    this.group.remove(g);
    g.traverse((o) => {
      if (o instanceof THREE.Mesh) o.geometry.dispose();
    });
    this.meshes.delete(key);
  }
}
